import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './ManagedCyberDefence.css';

const ManagedCyberDefence = () => {
    return (
        <>
            <Helmet>
                {/* Primary Meta Tags */}
                <title>24/7 Managed Cyber Defence Service - SOC as a Service | Epicfinal</title>
                <meta name="title" content="24/7 Managed Cyber Defence Service - SOC as a Service | Epicfinal" />
                <meta name="description" content="Managed Cyber Defence with 24/7 monitoring, threat detection & incident response for up to 50 endpoints. Windows & Linux support. Human-led investigations without building your own SOC." />
                <meta name="keywords" content="managed cyber defence, SOC as a service, managed security services, endpoint security, threat detection, incident response, ransomware protection, 24/7 monitoring, security operations center, MDR, managed detection response, Windows security, Linux security, endpoint protection" />
                <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
                <meta name="author" content="Epicfinal" />
                <link rel="canonical" href="https://epicfinal.com/managed-cyber-defence" />

                {/* Language & Locale */}
                <html lang="en" />
                <meta property="og:locale" content="en_US" />
                <link rel="alternate" href="https://epicfinal.com/managed-cyber-defence" hrefLang="en" />
                <link rel="alternate" href="https://epicfinal.com/managed-cyber-defence" hrefLang="x-default" />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://epicfinal.com/managed-cyber-defence" />
                <meta property="og:title" content="24/7 Managed Cyber Defence - SOC as a Service | Epicfinal" />
                <meta property="og:description" content="Managed Cyber Defence with 24/7 threat detection & response for up to 50 endpoints. Strengthen security without building a SOC." />
                <meta property="og:image" content="https://epicfinal.com/android-chrome-512x512.png" />
                <meta property="og:image:alt" content="Epicfinal Managed Cyber Defence - 24/7 Security Operations" />
                <meta property="og:site_name" content="Epicfinal" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:url" content="https://epicfinal.com/managed-cyber-defence" />
                <meta name="twitter:title" content="24/7 Managed Cyber Defence Service | Epicfinal" />
                <meta name="twitter:description" content="24/7 threat detection & incident response for up to 50 endpoints. Windows & Linux support." />
                <meta name="twitter:image" content="https://epicfinal.com/android-chrome-512x512.png" />

                {/* Schema.org Structured Data */}
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "Service",
                        "name": "Managed Cyber Defence Service",
                        "description": "24/7 managed security service with continuous monitoring, threat detection, and guided incident response for endpoints and identities",
                        "provider": {"@type": "Organization", "name": "Epicfinal", "url": "https://epicfinal.com"},
                        "serviceType": "Managed Security Service",
                        "areaServed": "Worldwide",
                        "url": "https://epicfinal.com/managed-cyber-defence"
                    }
                `}</script>
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        "name": "24/7 Managed Cyber Defence Service",
                        "description": "Enterprise-grade managed security with 24/7 monitoring, threat detection, and incident response for Windows and Linux endpoints.",
                        "url": "https://epicfinal.com/managed-cyber-defence",
                        "breadcrumb": {
                            "@type": "BreadcrumbList",
                            "itemListElement": [
                                {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://epicfinal.com"},
                                {"@type": "ListItem", "position": 2, "name": "Managed Services", "item": "https://epicfinal.com/managed-services"},
                                {"@type": "ListItem", "position": 3, "name": "Cyber Defence", "item": "https://epicfinal.com/managed-cyber-defence"}
                            ]
                        }
                    }
                `}</script>
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": [
                            {"@type": "Question", "name": "What is Managed Cyber Defence?", "acceptedAnswer": {"@type": "Answer", "text": "Managed Cyber Defence is a 24/7 security service providing continuous endpoint monitoring, threat detection, human-led investigations, and guided incident response without the need to build your own Security Operations Center (SOC)."}},
                            {"@type": "Question", "name": "How many endpoints are supported?", "acceptedAnswer": {"@type": "Answer", "text": "The service supports up to 50 endpoints including virtual machines, physical servers, and workstations running Windows or Linux operating systems."}},
                            {"@type": "Question", "name": "Is this cyber insurance?", "acceptedAnswer": {"@type": "Answer", "text": "No, this service does not provide financial coverage, legal coverage, or compensation for incidents. It does not replace a cyber insurance policy or any risk-transfer product."}}
                        ]
                    }
                `}</script>
            </Helmet>

            <div className="cyber-defence-page">
                {/* Hero Section */}
                <section className="cyber-hero">
                    <div className="container">
                        <div className="cyber-hero-content">
                            <div className="cyber-icon-badge">
                                <i className="las la-shield-alt"></i>
                            </div>
                            <h1 className="cyber-hero-title">
                                24/7 Managed Cyber Defence Service
                            </h1>
                            <p className="cyber-hero-subtitle">
                                Strengthen your security without building a SOC.
                            </p>
                            <p className="cyber-hero-description">
                                Our Managed Cyber Defence Service gives your business continuous monitoring, threat detection and guided response for endpoints and identities – delivered as a managed service, 24/7.
                            </p>
                            <p className="cyber-hero-description secondary">
                                We combine lightweight agents, modern security analytics and a dedicated security operations team so you can reduce risk, spot threats earlier and respond faster, without drowning in alerts.
                            </p>
                            <div className="cyber-hero-buttons">
                                <a href="https://my.epicfinal.com/index.php?rp=/store/managed-services/managed-cyber-defense-service" 
                                   className="btn-cyber btn-primary" target="_blank" rel="noopener noreferrer">
                                    Get Started
                                </a>
                                <Link to="/contact1" className="btn-cyber btn-secondary">
                                    Talk to Expert
                                </Link>
                            </div>
                            <div className="cyber-hero-badges">
                                <span className="badge"><i className="lab la-windows"></i> Windows</span>
                                <span className="badge"><i className="lab la-linux"></i> Linux</span>
                                <span className="badge"><i className="las la-desktop"></i> Up to 50 Endpoints</span>
                            </div>
                        </div>
                    </div>
                    <div className="cyber-hero-bg-elements">
                        <div className="hex hex-1"></div>
                        <div className="hex hex-2"></div>
                        <div className="hex hex-3"></div>
                    </div>
                </section>

                {/* Key Benefits Section */}
                <section className="cyber-benefits">
                    <div className="container">
                        <div className="cyber-section-header">
                            <h2>Key Benefits</h2>
                            <p>Enterprise-grade security without the enterprise complexity</p>
                        </div>
                        <div className="cyber-benefits-grid">
                            <div className="cyber-benefit-card">
                                <div className="cyber-benefit-icon">
                                    <i className="las la-eye"></i>
                                </div>
                                <h3>24/7 Monitoring of Endpoints</h3>
                                <p>Continuous visibility into suspicious behavior on supported Windows, macOS and Linux endpoints (where available), plus key identity signals from your environment.</p>
                            </div>
                            <div className="cyber-benefit-card">
                                <div className="cyber-benefit-icon">
                                    <i className="las la-user-secret"></i>
                                </div>
                                <h3>Human-Led Threat Investigations</h3>
                                <p>Potential threats are reviewed by security analysts, helping to distinguish real attacks from harmless noise so your team can stay focused on the important issues.</p>
                            </div>
                            <div className="cyber-benefit-card">
                                <div className="cyber-benefit-icon">
                                    <i className="las la-crosshairs"></i>
                                </div>
                                <h3>Early Detection of Serious Threats</h3>
                                <p>The service is designed to detect techniques used in real-world attacks – such as persistent footholds, malicious process behavior and signs of ransomware activity – to help you act before damage spreads.</p>
                            </div>
                            <div className="cyber-benefit-card">
                                <div className="cyber-benefit-icon">
                                    <i className="las la-file-medical-alt"></i>
                                </div>
                                <h3>Guided Containment and Remediation</h3>
                                <p>When a confirmed threat is identified, you receive a clear incident report with recommended actions so your team knows what to do next to contain and remove the threat.</p>
                            </div>
                            <div className="cyber-benefit-card">
                                <div className="cyber-benefit-icon">
                                    <i className="lab la-microsoft"></i>
                                </div>
                                <h3>Better Use of Microsoft Defender</h3>
                                <p>For environments using Microsoft Defender Antivirus, we can help apply recommended configurations and monitor for risky exclusions, so you get more value from tools you already own.</p>
                            </div>
                            <div className="cyber-benefit-card">
                                <div className="cyber-benefit-icon">
                                    <i className="las la-handshake"></i>
                                </div>
                                <h3>Straightforward, Service-Based Model</h3>
                                <p>You get a managed service with ongoing monitoring and expert support, not just another piece of software to deploy and maintain on your own.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* What the Service Does Section */}
                <section className="cyber-service-does">
                    <div className="container">
                        <div className="cyber-section-header light">
                            <h2>What the Service Does</h2>
                            <p>Comprehensive security coverage tailored to your environment</p>
                        </div>
                        <div className="cyber-service-list">
                            <div className="cyber-service-item">
                                <div className="cyber-service-check">
                                    <i className="las la-check"></i>
                                </div>
                                <p>Monitor supported endpoints and identities for suspicious behavior around the clock</p>
                            </div>
                            <div className="cyber-service-item">
                                <div className="cyber-service-check">
                                    <i className="las la-check"></i>
                                </div>
                                <p>Detect common attacker techniques such as persistence mechanisms and abuse of legitimate tools</p>
                            </div>
                            <div className="cyber-service-item">
                                <div className="cyber-service-check">
                                    <i className="las la-check"></i>
                                </div>
                                <p>Provide early warning indicators of possible ransomware activity</p>
                            </div>
                            <div className="cyber-service-item">
                                <div className="cyber-service-check">
                                    <i className="las la-check"></i>
                                </div>
                                <p>Highlight risky conditions on endpoints (for example, exposed services or insecure credential storage, where supported), so you can harden your environment</p>
                            </div>
                            <div className="cyber-service-item">
                                <div className="cyber-service-check">
                                    <i className="las la-check"></i>
                                </div>
                                <p>Deliver human-reviewed security incidents with plain-language explanations and practical next steps</p>
                            </div>
                            <div className="cyber-service-item">
                                <div className="cyber-service-check">
                                    <i className="las la-check"></i>
                                </div>
                                <p>Work alongside your existing IT and security processes rather than replace them</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Disclaimer Section */}
                <section className="cyber-disclaimer">
                    <div className="container">
                        <div className="cyber-disclaimer-card">
                            <div className="cyber-disclaimer-icon">
                                <i className="las la-info-circle"></i>
                            </div>
                            <div className="cyber-disclaimer-content">
                                <h3>This is not cyber insurance.</h3>
                                <p>The service does not provide any form of financial coverage, legal coverage or compensation for incidents. It does not replace a cyber insurance policy or any other risk-transfer product.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Level Up Section */}
                <section className="cyber-level-up">
                    <div className="container">
                        <div className="cyber-level-up-content">
                            <h2>A Simple Way to Level Up Your Security</h2>
                            <p className="intro-text">
                                Whether you have a small IT team or an established department, building a full 24/7 security operations capability on your own is expensive and time-consuming.
                            </p>
                            <div className="cyber-level-up-grid">
                                <div className="cyber-level-up-item">
                                    <i className="las la-clock"></i>
                                    <span>Always-on monitoring and investigations</span>
                                </div>
                                <div className="cyber-level-up-item">
                                    <i className="las la-user-shield"></i>
                                    <span>Access to specialist security expertise</span>
                                </div>
                                <div className="cyber-level-up-item">
                                    <i className="las la-file-alt"></i>
                                    <span>Clear incident reports and practical guidance</span>
                                </div>
                                <div className="cyber-level-up-item">
                                    <i className="las la-chart-line"></i>
                                    <span>A predictable service model without having to staff a SOC around the clock</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Pricing Section */}
                <section className="cyber-pricing">
                    <div className="container">
                        <div className="cyber-pricing-card">
                            <div className="cyber-pricing-header">
                                <h2>Managed Cyber Defence Service</h2>
                                <p>Comprehensive endpoint protection for your business</p>
                            </div>
                            <div className="cyber-pricing-body">
                                <ul className="cyber-pricing-features">
                                    <li><i className="las la-check"></i> 24/7 continuous monitoring</li>
                                    <li><i className="las la-check"></i> Threat detection and analysis</li>
                                    <li><i className="las la-check"></i> Human-led investigations</li>
                                    <li><i className="las la-check"></i> Guided incident response</li>
                                    <li><i className="las la-check"></i> Up to 50 endpoints (virtual machines / physical / workstations)</li>
                                    <li><i className="las la-check"></i> Windows and Linux support</li>
                                    <li><i className="las la-check"></i> Dedicated security team</li>
                                </ul>
                                <div className="cyber-pricing-platforms">
                                    <span className="platform-badge"><i className="lab la-windows"></i> Windows</span>
                                    <span className="platform-badge"><i className="lab la-linux"></i> Linux</span>
                                </div>
                                <a href="https://my.epicfinal.com/index.php?rp=/store/managed-services/managed-cyber-defense-service" 
                                   className="cyber-pricing-btn" target="_blank" rel="noopener noreferrer">
                                    Get Started Now
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="cyber-cta">
                    <div className="container">
                        <div className="cyber-cta-content">
                            <h2>Ready to Strengthen Your Security?</h2>
                            <p>Let our experts protect your business while you focus on what matters most</p>
                            <div className="cyber-cta-buttons">
                                <a href="https://my.epicfinal.com/index.php?rp=/store/managed-services/managed-cyber-defense-service" 
                                   className="btn-cyber btn-light" target="_blank" rel="noopener noreferrer">
                                    Order Service
                                </a>
                                <a href="https://meetings.hubspot.com/viacheslav-seifer" 
                                   className="btn-cyber btn-outline" target="_blank" rel="noopener noreferrer">
                                    Schedule Consultation
                                </a>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};

export default ManagedCyberDefence;

